'use strict'

const Big = require('big.js').default
const createMasterOracle = require('master-oracle-lib')
const { findByChainId } = require('chain-list')
const { fromUnit, handleForkedChainId } = require('metronome-utils')
const { findTokenByAddress } = require('token-list')
const pools = require('./pools')

const { getPoolVersion } = require('./utils')

const DEFAULT_VERSION = '1.3.0'

class ChainNotSupportedError extends Error {
  constructor(message) {
    super(message)
    this.name = 'ChainNotSupportedError'
  }
}

const merkleV2Addresses = {
  1: '0x293aaC1fef48b2ebf95d0CB3a31A7B219e8Ece9E' // Ethereum
}

/**
 * Like _.sumBy but using Big.
 *
 * @param {Array} array The array to iterate over.
 * @param {Function} iteratee The iteratee invoked per element.
 * @returns {Big} Returns the sum.
 *
 * @todo Move this to its own package.
 */
const bigSumBy = (array, iteratee) =>
  array.reduce((acc, s) => acc.plus(iteratee(s)), Big(0))

// Let's be sure we always return the same set of functions for the same
// given chain id, (regardless the options!).
const createMetronomeSynthInfoFunctions = function (chainId, web3, options) {
  const chainPools = pools[chainId]

  if (!chainPools) {
    throw new ChainNotSupportedError()
  }

  const {
    decimals: nativeTokenDecimals,
    nativeTokenSymbol,
    wrappedToken
  } = findByChainId(chainId)

  const handleWrappedToken = token =>
    token.symbol === wrappedToken.symbol
      ? {
          ...token,
          address: nativeTokenSymbol,
          decimals: nativeTokenDecimals,
          name: nativeTokenSymbol,
          symbol: nativeTokenSymbol
        }
      : token

  const getTotalSupplyInUsd = _tokens =>
    bigSumBy(_tokens, token =>
      Big(fromUnit(token.totalSupply, token.decimals)).mul(token.usdPrice)
    ).toFixed(2)

  const summarizePools = function (poolInfo) {
    const uniqueSynthetics = poolInfo.synthetics.reduce(function (
      acc,
      synthetic
    ) {
      if (!acc.find(({ address }) => address === synthetic.address)) {
        acc.push(synthetic)
      }
      return acc
    },
    [])
    const allCollaterals = poolInfo.collaterals

    return {
      minted: getTotalSupplyInUsd(uniqueSynthetics),
      tvl: getTotalSupplyInUsd(allCollaterals)
    }
  }

  const getPoolFunctions = function (poolAddress) {
    if (!chainPools.find(({ address }) => address === poolAddress)) {
      throw new Error('Pool not supported')
    }
    return (
      options.version
        ? Promise.resolve(options.version)
        : getPoolVersion(web3, poolAddress)
    ).then(function (version) {
      let abis
      try {
        abis = require(`./abi/${version}`)
      } catch (err) {
        abis = require(`./abi/${DEFAULT_VERSION}`)
      }
      const {
        crossChainDispatcherAbi,
        debtTokenAbi,
        depositTokenAbi,
        feeProviderAbi,
        merkleV2Abi,
        poolAbi,
        poolRegistryAbi,
        quoterAbi,
        rewardsDistributorAbi,
        swapperAbi,
        syntheticTokenAbi
      } = abis

      const pool = new web3.eth.Contract(poolAbi, poolAddress)

      return Promise.all([
        pool.methods.getDebtTokens().call(),
        pool.methods.getDepositTokens().call(),
        pool.methods.masterOracle().call(),
        pool.methods.poolRegistry().call()
      ]).then(
        ([
          debtTokens,
          depositTokens,
          masterOracleAddress,
          poolRegistryAddress
        ]) =>
          Promise.all([
            createMasterOracle(web3, { address: masterOracleAddress }),
            ...debtTokens.map(function (debtToken) {
              const debtContract = new web3.eth.Contract(
                debtTokenAbi,
                debtToken
              )
              return Promise.all([
                debtContract.methods.syntheticToken().call(),
                debtContract.methods.interestRate().call(),
                debtContract.methods.maxTotalSupply().call(),
                debtContract.methods.totalSupply().call()
              ]).then(
                ([
                  syntheticToken,
                  interestRate,
                  debtMaxTotalSupply,
                  debtTotalSupply
                ]) => ({
                  debtMaxTotalSupply,
                  debtToken,
                  debtTotalSupply,
                  interestRate,
                  syntheticToken
                })
              )
            })
          ]).then(function ([masterOracle, ...tokens]) {
            const { getPriceInUsd } = masterOracle

            const syntheticTokens = tokens.map(
              ({ syntheticToken }) => syntheticToken
            )

            const getCollateralTokenStaticInfo = function (
              depositTokenAddress
            ) {
              const depositTokenContract = new web3.eth.Contract(
                depositTokenAbi,
                depositTokenAddress
              )
              return Promise.all([
                depositTokenContract.methods.underlying().call(),
                depositTokenContract.methods.collateralFactor().call(),
                depositTokenContract.methods.maxTotalSupply().call()
              ]).then(function ([token, collateralFactor, maxTotalSupply]) {
                const { address, decimals, name, symbol } = findTokenByAddress(
                  token,
                  chainId
                )
                return {
                  address,
                  collateralFactor: Big(collateralFactor).div(1e16).toFixed(),
                  decimals,
                  depositToken: depositTokenAddress,
                  maxTotalSupply,
                  name,
                  symbol
                }
              })
            }

            const getCollateralTokensInfo = () =>
              Promise.all(
                depositTokens.map(function (depositTokenAddress) {
                  const depositTokenContract = new web3.eth.Contract(
                    depositTokenAbi,
                    depositTokenAddress
                  )
                  return Promise.all([
                    getCollateralTokenStaticInfo(depositTokenAddress),
                    depositTokenContract.methods.totalSupply().call()
                  ]).then(function ([staticInfo, totalSupply]) {
                    const { address, maxTotalSupply } = staticInfo
                    return getPriceInUsd(address)
                      .then(usdPrice => ({
                        ...staticInfo,
                        availableSupply: Big(maxTotalSupply)
                          .minus(totalSupply)
                          .toFixed(),
                        totalSupply,
                        usdPrice: fromUnit(usdPrice, 18)
                      }))
                      .then(handleWrappedToken)
                  })
                })
              )

            const getSyntheticTokenStaticInfo = function (syntheticToken) {
              const syntheticTokenContract = new web3.eth.Contract(
                syntheticTokenAbi,
                syntheticToken
              )
              return Promise.all([
                syntheticTokenContract.methods.bridgedInSupply().call(),
                syntheticTokenContract.methods.bridgedOutSupply().call(),
                syntheticTokenContract.methods.decimals().call(),
                syntheticTokenContract.methods.maxBridgedInSupply().call(),
                syntheticTokenContract.methods.maxBridgedOutSupply().call(),
                syntheticTokenContract.methods.maxTotalSupply().call(),
                syntheticTokenContract.methods.name().call(),
                syntheticTokenContract.methods.symbol().call()
              ]).then(
                ([
                  bridgedInSupply,
                  bridgedOutSupply,
                  decimals,
                  maxBridgedInSupply,
                  maxBridgedOutSupply,
                  maxTotalSupply,
                  name,
                  symbol
                ]) => ({
                  address: syntheticToken,
                  bridgedInSupply,
                  bridgedOutSupply,
                  decimals: parseInt(decimals),
                  maxBridgedInSupply,
                  maxBridgedOutSupply,
                  maxTotalSupply,
                  name,
                  symbol
                })
              )
            }

            const getSyntheticTokensInfo = () =>
              Promise.all(
                tokens.map(function ({
                  syntheticToken,
                  interestRate,
                  debtMaxTotalSupply,
                  debtTotalSupply
                }) {
                  const syntheticTokenContract = new web3.eth.Contract(
                    syntheticTokenAbi,
                    syntheticToken
                  )
                  return Promise.all([
                    getSyntheticTokenStaticInfo(syntheticToken),
                    syntheticTokenContract.methods.totalSupply().call(),
                    getPriceInUsd(syntheticToken)
                  ]).then(function ([staticInfo, totalSupply, usdPrice]) {
                    const { maxTotalSupply } = staticInfo
                    return {
                      ...staticInfo,
                      availableSupply: Big(maxTotalSupply)
                        .minus(totalSupply)
                        .toFixed(),
                      debtAvailableSupply: Big(debtMaxTotalSupply)
                        .minus(debtTotalSupply)
                        .toFixed(),
                      interestRate: Big(interestRate)
                        .div(1e18)
                        .mul(100)
                        .toFixed(),
                      totalSupply,
                      usdPrice: fromUnit(usdPrice, 18)
                    }
                  })
                })
              )

            const poolRegistry = new web3.eth.Contract(
              poolRegistryAbi,
              poolRegistryAddress
            )

            const getNativeTokenGateway = () =>
              poolRegistry.methods.nativeTokenGateway().call()

            const getRouting = (tokenIn, tokenOut) =>
              poolRegistry.methods
                .swapper?.()
                .call()
                .then(function (swapperAddress) {
                  const swapperContract = new web3.eth.Contract(
                    swapperAbi,
                    swapperAddress
                  )
                  return swapperContract.methods
                    .getRouting(0, tokenIn, tokenOut)
                    .call()
                })

            const getSynthFees = () =>
              pool.methods
                .feeProvider()
                .call()
                .then(function (feeProviderAddress) {
                  const feeProvider = new web3.eth.Contract(
                    feeProviderAbi,
                    feeProviderAddress
                  )
                  // v1.3.2+ removed the per-user esMET fee-discount model:
                  // `defaultSwapFee()` and `getTiers()` no longer exist (swap
                  // fees are now per synth-pair, read via `swapFees(in,out)`).
                  // Fall back to neutral values so the `fees` shape is stable.
                  //
                  // We branch on ABI-method presence, but that only reflects the
                  // Pool VERSION() used to pick the abi folder -- if a pool
                  // resolves the legacy 1.3.0 abi against upgraded 1.3.2
                  // contracts (e.g. the 1.3.2 folder failed to load and it fell
                  // back to DEFAULT_VERSION), these calls revert on-chain. Catch
                  // that so getInfo() never fails to load over a missing fee.
                  const hasLegacySwapFee = !!feeProvider.methods.defaultSwapFee
                  return Promise.all([
                    hasLegacySwapFee
                      ? feeProvider.methods
                          .defaultSwapFee()
                          .call()
                          .catch(() => '0')
                      : Promise.resolve('0'),
                    feeProvider.methods.depositFee().call(),
                    hasLegacySwapFee
                      ? feeProvider.methods
                          .getTiers()
                          .call()
                          .catch(() => [])
                      : Promise.resolve([]),
                    feeProvider.methods.liquidationFees().call(),
                    feeProvider.methods.issueFee().call(),
                    feeProvider.methods.repayFee().call(),
                    feeProvider.methods.withdrawFee().call()
                  ]).then(
                    ([
                      defaultSwapFee,
                      depositFee,
                      tiers,
                      liquidationFees,
                      issueFee,
                      repayFee,
                      withdrawFee
                    ]) => ({
                      defaultSwapFee: fromUnit(defaultSwapFee),
                      depositFee: fromUnit(depositFee),
                      feeProviderAddress,
                      issueFee: fromUnit(issueFee),
                      liquidationFees: {
                        liquidatorFee: fromUnit(
                          liquidationFees.liquidatorIncentive
                        ),
                        protocolFee: fromUnit(liquidationFees.protocolFee)
                      },
                      repayFee: fromUnit(repayFee),
                      tiers,
                      withdrawFee: fromUnit(withdrawFee)
                    })
                  )
                })

            const isActive = function (address, type) {
              const contract = new web3.eth.Contract(
                abis[`${type}Abi`],
                address
              )
              return contract.methods.isActive().call()
            }

            // Gets a list of deposit or synthetic tokens and adds their
            // activation status.
            const addActivationState = (_tokens, type) =>
              Promise.all(
                _tokens.map(token =>
                  isActive(
                    type === 'depositToken'
                      ? token.depositToken
                      : token.address,
                    type
                  ).then(_isActive => ({
                    ...token,
                    isActive: _isActive
                  }))
                )
              )

            // Resilient across contract versions:
            // - v1.3.2+ exposes `isDestinationChainSupported` on the PoolRegistry
            // - older versions read it from the (now removed) CrossChainDispatcher
            // Any failure defaults to `false` so the main info fetch never breaks.
            const isDestinationChainSupported = id =>
              Promise.resolve()
                .then(function () {
                  if (poolRegistry.methods.isDestinationChainSupported) {
                    return poolRegistry.methods
                      .isDestinationChainSupported(id)
                      .call()
                  }
                  return poolRegistry.methods
                    .crossChainDispatcher()
                    .call()
                    .then(function (crossChainDispatcherAddress) {
                      const crossChainDispatcherContract =
                        new web3.eth.Contract(
                          crossChainDispatcherAbi,
                          crossChainDispatcherAddress
                        )
                      return crossChainDispatcherContract.methods
                        .isDestinationChainSupported(id)
                        .call()
                    })
                })
                .catch(() => false)

            // Resilient across contract versions:
            // - v1.3.2+ exposes `isBridgingActive` directly on the PoolRegistry
            // - older versions read it from the (now removed) CrossChainDispatcher
            // Bridging status defaults to `false` on any failure so that the
            // core pool status (paused/stopped/swap) always loads.
            const getIsBridgingActive = () =>
              Promise.resolve()
                .then(function () {
                  if (poolRegistry.methods.isBridgingActive) {
                    return poolRegistry.methods.isBridgingActive().call()
                  }
                  return poolRegistry.methods
                    .crossChainDispatcher()
                    .call()
                    .then(function (crossChainDispatcherAddress) {
                      const crossChainDispatcherContract =
                        new web3.eth.Contract(
                          crossChainDispatcherAbi,
                          crossChainDispatcherAddress
                        )
                      return crossChainDispatcherContract.methods
                        .isBridgingActive()
                        .call()
                    })
                })
                .catch(() => false)

            const getSynthStatus = () =>
              Promise.all([
                pool.methods.paused().call(),
                pool.methods.everythingStopped().call(),
                pool.methods.isSwapActive().call(),
                getIsBridgingActive()
              ]).then(
                ([
                  _paused,
                  _everythingStopped,
                  _isSwapActive,
                  isBridgingActive
                ]) => ({
                  everythingStopped: _everythingStopped,
                  isBridgingActive,
                  isSwapActive: _isSwapActive,
                  paused: _paused
                })
              )

            /**
             * Returns an array of rewardsDistributors. They include the ones from the pool, and the merkle ones.
             * @returns {Promise<[{address: string, rewardsDistributor: Contract, rewardToken: string, type: string}]>}
             */
            const getRewardsDistributors = () =>
              pool.methods
                .getRewardsDistributors()
                .call()
                .then(rewardsDistributors =>
                  Promise.all(
                    rewardsDistributors.map(function (rdAddress) {
                      const rewardsDistributor = new web3.eth.Contract(
                        rewardsDistributorAbi,
                        rdAddress
                      )
                      return rewardsDistributor.methods
                        .rewardToken()
                        .call()
                        .then(rewardToken => ({
                          address: rdAddress,
                          rewardsDistributor,
                          rewardToken,
                          type: 'pool'
                        }))
                    })
                  )
                )
                .then(function (rewardDistributors) {
                  const merkleContractAddress = merkleV2Addresses[chainId]
                  if (!merkleContractAddress || !options.merkleRewardsEnabled) {
                    return rewardDistributors
                  }
                  const merkleContract = new web3.eth.Contract(
                    merkleV2Abi,
                    merkleContractAddress
                  )
                  return merkleContract.methods
                    .token()
                    .call()
                    .then(merkleRewardToken => [
                      ...rewardDistributors,
                      {
                        address: merkleContractAddress,
                        rewardsDistributor: merkleContract,
                        rewardToken: merkleRewardToken,
                        type: 'merkle'
                      }
                    ])
                })

            const getRewardTokens = () =>
              getRewardsDistributors().then(rd =>
                rd.map(({ rewardToken }) => rewardToken)
              )

            const getInfo = () =>
              Promise.all([
                getSynthStatus(),
                getCollateralTokensInfo(),
                getSyntheticTokensInfo(),
                getSynthFees(),
                getRewardTokens()
              ]).then(([status, collaterals, synthetics, fees, rewards]) =>
                Promise.all([
                  addActivationState(collaterals, 'depositToken'),
                  addActivationState(synthetics, 'syntheticToken')
                ]).then(([_collaterals, _synthetics]) => ({
                  collaterals: _collaterals,
                  fees,
                  rewards,
                  status,
                  summary: {
                    minted: getTotalSupplyInUsd(synthetics),
                    tvl: getTotalSupplyInUsd(collaterals)
                  },
                  synthetics: _synthetics
                }))
              )

            const getLeverageSwapAndCallbackLzArgs = (
              sourceChainId,
              destinationChainId
            ) =>
              poolRegistry.methods
                .quoter()
                .call()
                .then(function (quoterAddress) {
                  const quoterContract = new web3.eth.Contract(
                    quoterAbi,
                    quoterAddress
                  )
                  return quoterContract.methods
                    .getLeverageSwapAndCallbackLzArgs(
                      sourceChainId,
                      destinationChainId
                    )
                    .call()
                })

            const quoteCrossChainLeverageNativeFee = function (
              lzArgs,
              syntheticToken
            ) {
              const syntheticTokenContract = new web3.eth.Contract(
                syntheticTokenAbi,
                syntheticToken
              )
              return Promise.all([
                poolRegistry.methods.quoter().call(),
                syntheticTokenContract.methods.proxyOFT().call()
              ]).then(function ([quoterAddress, proxyOFT]) {
                const quoterContract = new web3.eth.Contract(
                  quoterAbi,
                  quoterAddress
                )
                return quoterContract.methods
                  .quoteCrossChainLeverageNativeFee(proxyOFT, lzArgs)
                  .call()
              })
            }

            return {
              ...chainPools.find(({ address }) => address === poolAddress),
              debtTokens,
              depositTokens,
              getCollateralTokensInfo,
              getInfo,
              getLeverageSwapAndCallbackLzArgs,
              getNativeTokenGateway,
              getRewardsDistributors,
              getRewardTokens,
              getRouting,
              getSyntheticTokensInfo,
              getSynthFees,
              isDestinationChainSupported,
              masterOracle,
              poolAddress,
              quoteCrossChainLeverageNativeFee,
              syntheticTokens
            }
          })
      )
    })
  }

  return options.poolAddress
    ? getPoolFunctions(options.poolAddress)
    : Promise.all(
        chainPools.map(({ address }) => getPoolFunctions(address))
      ).then(poolFunctions => ({
        pools: poolFunctions,
        summarizePools
      }))
}

const createMetronomeSynthInfo = (web3, options = {}) =>
  web3.eth
    .getChainId()
    .then(handleForkedChainId)
    .then(chainId => createMetronomeSynthInfoFunctions(chainId, web3, options))

module.exports = createMetronomeSynthInfo
